import { Injectable } from '@angular/core';
import { logError } from '@office-start/telemetry-service';
import { KnownErrorInstrumentationEvents, KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { HttpStatusCode } from '../constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp } from '../instrumentation/instrumented-http.service';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { ContextService } from '../shared-context/context.service';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { AuthState, AuthStateService } from './auth-state.service';
import { ConfigParserService } from './config-parser.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { UserDataStorageConfigService } from './user-data-storage-config.service';
import { UserDataStorageService } from './user-data-storage.service';

interface UserStorageCache {
    firstUse: boolean;
    firstUseTimestamp: number;
    content: { [key: string]: any };
}

// TODO: Refactor to use XhrService
@Injectable({
    providedIn: 'root'
})
export class OfficeHomeStorageService implements UserDataStorageService {

    private readonly serviceUrl: string;
    private readonly firstUse: boolean;
    private readonly firstUseTimestamp: number;
    private readonly masterCollection: { [key: string]: any };

    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly contextService: ContextService,
        private readonly instrumentationService: InstrumentationService,
        private readonly instrumentedHttp: InstrumentedHttp,
        private readonly authStateService: AuthStateService,
        private readonly officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly userDataStorageConfigService: UserDataStorageConfigService,
        configParserService: ConfigParserService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly scheduler: SchedulerService
    ) {
        if (!(typeof contextService.sharedContext.officeHomeApiRoot === 'undefined' || contextService.sharedContext.officeHomeApiRoot === null) &&
            userDataStorageConfigService.config &&
            !(typeof userDataStorageConfigService.config.userStorageEndpoint === 'undefined' || userDataStorageConfigService.config.userStorageEndpoint === null)) {
            this.serviceUrl = `${contextService.sharedContext.officeHomeApiRoot}/${userDataStorageConfigService.config.userStorageEndpoint}`;
        }

        const userStorageCache = configParserService.parse<UserStorageCache>('userStorageCache') || { firstUse: false, firstUseTimestamp: 0, content: {} };
        this.firstUse = userStorageCache.firstUse;
        this.firstUseTimestamp = userStorageCache.firstUseTimestamp;
        this.masterCollection = userStorageCache.content;
    }

    public get<T>(key: string, remoteAttempts: number = 1): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            // Return from in-memory cache if it exists
            if (this.masterCollection.hasOwnProperty(key)) {
                const value = { ...this.masterCollection[key] };
                resolve(value as T);
                return;
            }

            // Short-circuit the case when it is known that storage does not contain any values yet or no remote fetch is desired
            if (this.firstUse || remoteAttempts < 1) {
                // Return undefined as indicator that value has not been found
                resolve(undefined);
                return;
            }

            // Check current auth state: if user is no longer authenticated then fail fast without
            // making server call
            if (this.authStateService.getCurrentState() !== AuthState.SignedIn) {
                reject(`User has to be signed in to access storage for <${key}> key.`);
                return;
            }

            // Check if there is remote service
            if (!this.serviceUrl) {
                reject('User storage does not support roaming');
                return;
            }

            let retryAttempt = 0;

            const getImplementation = (eventName: KnownInstrumentationEvents, otelEventName: KnownRequestInstrumentationEvents, requestConfig?: HttpRequestOptions) => {
                // IMPORTANT: Note that url has trailing '/', this is required because key can have '.' symbol and IIS may interpret
                // the url as file location. This will certainly return 404 and IIS error page
                // IMPORTANT: Metrics are not recorded on purpose. This is done due to the fact that setting metrics
                // will invalidate user profile. This will trigger user storage write operation for the updated user profile.
                // This will create self-feeding cycle:
                //      user storage read operation -> instrumented with metrics ->
                //      user profile updated -> user profile stored -> user storage write operation
                this.instrumentedHttp
                    .get<T>(eventName, otelEventName, `${this.serviceUrl}/${key}/`, requestConfig || {}, FetchStrategy.NetworkOnly)
                    .instrument((_result, params) => {
                        params[InstrumentationParam.ElementId] = key;
                        params[InstrumentationParam.Retry] = retryAttempt.toString();
                    })
                    .then(result => resolve(result.data))
                    .catch(error => {
                        // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                        this.globalErrorHandler.handleError(error, ErrorId.Bn3rc);

                        // NotFound (404) is expected result indicating that storage does not contain
                        // value for requested key; this is valid response
                        if (error && error.status === HttpStatusCode.NotFound) {
                            // Return undefined as indicator that value has not been found
                            resolve(undefined);
                        } else {
                            // Forbidden (403) is returned when request does not carry valid auth cookie
                            // (expired or cleared) and it is not retriable
                            if (error.status !== HttpStatusCode.Forbidden && retryAttempt < (remoteAttempts - 1)) {
                                retryAttempt++;
                                getImplementation(eventName, otelEventName, requestConfig);
                            } else {
                                reject(`Failed to get value from user data storage for <${key}> key. Status code: ${error.status}`);
                            }
                        }
                    });
            };

            this.request(getImplementation, KnownInstrumentationEvents.OfficeHomeStorageReadRequest, KnownRequestInstrumentationEvents.OfficeHomeStorageReadRequest, reject);
        });
    }

    public set<T>(key: string, value: T): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // Check current auth state: if user is no longer authenticated then fail fast without
            // making server call
            if (this.authStateService.getCurrentState() !== AuthState.SignedIn) {
                reject(`User has to be signed in to access storage for <${key}> key.`);
                return;
            }

            // Cache supplied value in memory for quick retrieval
            this.masterCollection[key] = { ...value as any };

            // Check if there is remote service
            if (!this.serviceUrl) {
                reject('User storage does not support roaming');
                return;
            }

            // IMPORTANT: Note that url has trailing '/', this is required because key can have '.' symbol and IIS may interpret
            // the url as file location. This will certainly return 404 and IIS error page
            // IMPORTANT: Metrics are not recorded on purpose. This is done due to the fact that setting metrics
            // will invalidate user profile. This will trigger user storage write operation for the updated user profile.
            // This will create self-feeding cycle:
            //      user storage read operation -> instrumented with metrics ->
            //      user profile updated -> user profile stored -> user storage write operation
            const setImplementation = (eventName: KnownInstrumentationEvents, otelEventName: KnownRequestInstrumentationEvents, requestConfig?: HttpRequestOptions) => {
                requestConfig = requestConfig || {};
                if (!requestConfig.headers) {
                    requestConfig.headers = new Map<string, string>();
                }

                if (!this.officeHomeApiConfigService.config.jwtAuthEnabled && !this.contextService.sharedContext.inMockMode) {
                    const csrfToken = this.contextService.getCsrfToken();
                    if (csrfToken) {
                        requestConfig.headers.set('AjaxSessionKey', this.contextService.getCsrfToken());
                    } else {
                        this.instrumentationService.logErrorEvent(KnownInstrumentationEvents.CsrfTokenError);
                        logError({
                            eventName: KnownErrorInstrumentationEvents.CsrfTokenError,
                            errorType: 'User_Storage_Error'
                        });
                        reject('CSRF token missing');
                        return;
                    }
                }

                const writeToStorage = () => this.instrumentedHttp.put<T>(eventName, otelEventName, `${this.serviceUrl}/${key}/`, value, requestConfig)
                    .instrument((_result, params) => {
                        params[InstrumentationParam.ElementId] = key;
                    })
                    .then(() => resolve(null))
                    .catch(error => {
                        // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                        this.globalErrorHandler.handleError(error, ErrorId.J7GBR);

                        reject(`Failed to update or create value in roaming user data storage for <${key}> key. Status code: ${error.status}`);
                    });

                if (this.userDataStorageConfigService.config.deferWrite) {
                    this.scheduler.scheduleWork(() => {
                        writeToStorage();
                    }, SchedulePriority.VeryLow);
                } else {
                    writeToStorage();
                }
            };

            this.request(setImplementation, KnownInstrumentationEvents.OfficeHomeStorageWriteRequest, KnownRequestInstrumentationEvents.OfficeHomeStorageWriteRequest, reject);
        });
    }

    public request(request: (eventName: KnownInstrumentationEvents, otelEventName: KnownRequestInstrumentationEvents, config?: HttpRequestOptions) => void, requestName: KnownInstrumentationEvents, otelRequestName: KnownRequestInstrumentationEvents, reject: any) {
        if (!this.officeHomeApiConfigService.config.jwtAuthEnabled) {
            request(requestName, otelRequestName);
        } else {
            this.accessTokenService.getAccessToken(AccessTokenConsumer.OfficeHome).then(tokenResponse => {
                if (!tokenResponse.success) {
                    this.instrumentationService.logRequestEvent(requestName, HttpStatusCode.TokenUnavailable, -1, typeof tokenResponse.error === 'string' ? tokenResponse.error : '');
                    reject('Token Unavailable');
                    return;
                }

                const requestConfig = {
                    headers: new Map([
                        ['Authorization', `Bearer ${tokenResponse.token}`] // TODO: Handle msa/rps tokens
                    ])
                };
                request(requestName, otelRequestName, requestConfig);
            });
        }
    }

    public isFirstUse(): boolean {
        return this.firstUse;
    }

    public getFirstUseTimestamp(): number {
        return this.firstUseTimestamp;
    }
}
